import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faShareAlt,
  faShoppingCart,
  faTimes
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CartProvider from '@jetshop/core/components/Query/CartProvider';
import t from '@jetshop/intl';
import Drawer, { DrawerTarget } from '@jetshop/ui/Modal/Drawer';
import theme from '@jetshop/ui/utils/theme';
import get from 'lodash.get';
import { Fragment, default as React, useState } from 'react';
import styled from 'react-emotion';
import { useChannel } from '../../hooks/useChannel';
import { SharedCartLink } from '../CategoryPage/SharedCart/SharedCartLink';
import { CloseIcon, MenuTitle } from '../Layout/Header/Header';
import { Price } from '../Price';
import { VatContext } from '../Price/VatContext';
import Button from '../ui/Button';
import CartItem from './CartItem';
import cartQuery from './CartQuery.gql';

library.add(faShoppingCart, faTimes, faShareAlt);

const Flyout = styled('div')`
    background: white;
    color: ${theme('colors.black')};

    .scroll-wrapper {
        overflow: auto;
        max-height: 73vh;
    }

    ${({ theme }) => theme.above.md} {
        position: absolute;
        z-index: 999;
        right: ${({ isOpen }) => (isOpen ? 0 : '-20rem')};
        top: 0;
        height: 100rem !important;
        padding: 1rem;
        transition: right 0.3s ease;

        .scroll-wrapper {
            max-height: 90vh;

            > section {
                max-height: 900px;
            }
        }

        &.skoterdelen {
            width: 40rem;
        }

        &.weAhl {
            width: 100%;
        }
    }

    padding: 20px;
`;

const Header = styled('header')`
    position: relative;
    text-align: left;
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 0 0 1rem 0;
    margin: 0 0 1rem 0;

    h2 {
        font-size: 1.5rem;
        text-transform: uppercase;

        ${theme('below.md')} {
            font-size: 1.5rem;
        }
    }
`;

const CartIconWrapper = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
`;

const CartHeaderIcon = styled(FontAwesomeIcon)`
    font-size: 2rem;
    margin-right: 10px;
`;

const CartIconAmount = styled('span')`
    position: absolute;
    left: 40%;
    top: 20%;

    color: ${theme('colors.black')};
    font-size: 0.75rem;
    font-weight: bold;

    &.two {
        left: 33%;
    }

    &.three {
        left: 27%;
    }
`;

const CartIconShareButton = styled('button')`
    background: none;
    color: ${theme('colors.black')};
    font-weight: bold;
    font-size: 1rem;
    text-transform: uppercase;
    text-underline-offset: 0.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    margin-left: auto;

    &:hover {
        text-decoration: underline;
    }

    ${({ theme }) => theme.above.md} {
        margin-left: auto;
        margin-right: 60px;
    }
`;

const CartItems = styled('section')`
    max-height: 280px;
    min-height: 160px;
    overflow-y: auto;
    height: auto;
    padding: 0;
`;

const Summary = styled('section')`
    background: #fff;
    margin: 0;

    a {
        width: 100%;
        color: white;

        :hover {
            color: white;
        }
    }
`;

const SummaryTitle = styled('div')`
    font-size: 1.4rem;
    text-transform: uppercase;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ddd;
`;

const SummaryRow = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const DeliverySummary = styled('div')`
    text-align: center;
    margin-bottom: 1rem;
`;
const DeliverySummaryText = styled('span')`
    font-size: 1.2rem;
    color: ${theme('colors.green')};
    text-transform: uppercase;
`;

const TotalSummary = styled('div')`
    font-size: 1.2rem;
    color: ${theme('colors.black')};
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #ddd;
`;

const CheckoutButtonWrapper = styled('div')`
    ${theme('below.md')} {
        padding-bottom: 60px;
    }

    a {
        width: 100%;
        color: white;
        font-family: 'Abel', sans-serif;
        font-size: 1.3rem;

        :hover {
            color: white;
        }
    }
`;

const Checkout = styled(Button)`
    width: 100%;
    display: flex;
    background: ${theme('colors.green')};
    color: white;
    text-transform: uppercase;
`;

const DiscountTitle = styled('span')`
    color: ${theme('colors.discountedPrice')};
`;
const DiscountPrice = styled(Price)`
    color: ${theme('colors.discountedPrice')};
`;

const CloseModal = styled('button')`
    position: absolute;
    top: 0;
    right: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    appearance: none;
    background: none;
    display: none;

    ${({ theme }) => theme.above.md} {
        display: flex;
    }
`;
const EmptyButtonIcon = styled(FontAwesomeIcon)`
    font-size: 2rem;
    color: #a5a5a5;
`;

function getPrice(items, before, getDiscount) {

  let priceBefore = {
    incVat: 0,
    exVat: 0,
    vat: 0
  };
  let priceNow = {
    incVat: 0,
    exVat: 0,
    vat: 0
  };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const quantity = item.quantity;


    if (item.variant) {
      priceBefore.incVat =
        priceBefore.incVat + item.variant.price.incVat * quantity;
      priceBefore.exVat = priceBefore.exVat + item.variant.price.exVat * quantity;
      priceBefore.vat = priceBefore.vat + item.variant.price.vat * quantity;
    } else {
      priceBefore.incVat =
        priceBefore.incVat + item.product.price.incVat * quantity;
      priceBefore.exVat = priceBefore.exVat + item.product.price.exVat * quantity;
      priceBefore.vat = priceBefore.vat + item.product.price.vat * quantity;
    }

    if (item.variant) {
      priceNow.incVat =
        priceNow.incVat + item.variant.price.incVat * quantity;
      priceNow.exVat = priceNow.exVat + item.variant.price.exVat * quantity;
      priceNow.vat = priceNow.vat + item.variant.price.vat * quantity;
    } else {
      priceNow.incVat =
        priceNow.incVat + item.product.price.incVat * quantity;
      priceNow.exVat = priceNow.exVat + item.product.price.exVat * quantity;
      priceNow.vat = priceNow.vat + item.product.price.vat * quantity;
    }
  }

  if (getDiscount) {
    return {
      incVat: priceBefore.incVat - priceNow.incVat,
      exVat: priceBefore.exVat - priceNow.exVat,
      vat: priceBefore.vat - priceNow.vat
    };
  } else if (before) {
    return priceBefore;
  } else {
    return priceNow;
  }
}

const CartFlyoutView = ({ cart, modal, isOpen, ...rest }) => {
  const { isWeAhl } = useChannel();
  const [shared, setShared] = useState(false);
  let items = get(cart, 'data.cart.items', []);

  // PUT DELIVERY ITEM AT THE END OF THE CART
  const deliveryItem = items.find(
    (item) => item.product?.articleNumber === 'hemleverans'
  );
  items = items.filter((item) => item.product?.articleNumber !== 'hemleverans');
  if (deliveryItem) {
    items.push(deliveryItem);
  }

  const rawCheckoutUrl = get(cart, 'data.cart.externalCheckoutUrl');
  let checkoutUrl = rawCheckoutUrl;
  const urlComponents = checkoutUrl?.split('&');

  if (urlComponents) {
    urlComponents.forEach((component, index) => {
      if (component.includes('currency')) {
        urlComponents[index] = 'currency=SEK';
      } else if (component.includes('country')) {
        urlComponents[index] = 'country=SE';
      }
    });
    checkoutUrl = urlComponents.join('&');
  }

  if (items.length === 0) {
    return (
      <Flyout
        isOpen={isOpen}
        {...rest}
        className={isWeAhl ? 'weAhl' : 'skoterdelen'}
      >
        <div css={{ padding: '2em' }}>{t('No items in cart.')}</div>
      </Flyout>
    );
  }

  const totalCartItems = items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <Flyout
      isOpen={isOpen}
      {...rest}
      className={isWeAhl ? 'weAhl' : 'skoterdelen'}
    >
      <div className={'scroll-wrapper'}>
        <Header>
          <CartIconWrapper>
            <CartHeaderIcon icon={['fal', 'shopping-cart']} />
            {!isWeAhl ? (
              <>
                <CartIconAmount
                  className={
                    totalCartItems >= 10
                      ? totalCartItems >= 100
                        ? 'three'
                        : 'two'
                      : 'one'
                  }
                >
                  {totalCartItems}
                </CartIconAmount>
              </>
            ) : null}
          </CartIconWrapper>
          <h2>
            Din kundvagn
            <VatContext.Consumer>
              {({ incVat }) => (
                <small
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    textTransform: 'none'
                  }}
                >
                  {incVat
                    ? 'Priserna visas inkl. moms'
                    : 'Priserna visas exkl. moms'}
                </small>
              )}
            </VatContext.Consumer>
          </h2>
          <CartIconShareButton onClick={() => setShared(!shared)}>
            <FontAwesomeIcon
              style={{ marginRight: 8, fontSize: '20px' }}
              icon={['fal', 'share-alt']}
            />
            Dela
          </CartIconShareButton>
          <CloseModal onClick={modal.hideTarget}>
            <EmptyButtonIcon icon={['fal', 'times']} />{' '}
          </CloseModal>
        </Header>

        {shared ? <SharedCartLink products={items} /> : null}

        <CartItems data-testid='cart-item-wrapper'>
          {items.map((item) => (
            <CartItem item={item} key={item.id} />
          ))}
        </CartItems>

        <Summary>
          <SummaryTitle>Summa</SummaryTitle>
          <SummaryRow>
            <span>Ordinarie pris:</span>
            <div>
              <VatContext.Consumer>
                {({ incVat }) => (
                  <Price includeVat={incVat} price={getPrice(items, true)} />
                )}
              </VatContext.Consumer>
            </div>
          </SummaryRow>

          {getPrice(items, true, true).incVat > 0 && (
            <SummaryRow>
              <DiscountTitle>Produktrabatt:</DiscountTitle>
              <VatContext.Consumer>
                {({ incVat }) => (
                  <DiscountPrice
                    includeVat={incVat}
                    price={getPrice(items, true, true)}
                  />
                )}
              </VatContext.Consumer>
            </SummaryRow>
          )}

          <TotalSummary>
            <label>Totalt:</label>
            <VatContext.Consumer>
              {({ incVat }) => (
                <Price
                  includeVat={incVat}
                  price={cart.data.cart.productPreviousTotal}
                />
              )}
            </VatContext.Consumer>
          </TotalSummary>

          {/* <DeliverySummary>
            {cart.data.cart.productTotal.incVat >= 2000 && (
              <DeliverySummaryText>Vi bjuder på frakten!</DeliverySummaryText>
            )}
          </DeliverySummary> */}
        </Summary>

        {/* {checkoutUrl && (
        <Analytics>
          {track => (
            <Checkout
              anchor={true}
              href={checkoutUrl}
              onClick={() => {
                track(trackCartCheckout({ cart: cart.data.cart }));
              }}
            >
              {t('Check out')}
            </Checkout>
          )})
        </Analytics>
      )} */}

        <CheckoutButtonWrapper>
          <Checkout anchor={true} href={checkoutUrl}>
            {t('Check out')}
          </Checkout>
        </CheckoutButtonWrapper>
      </div>
    </Flyout>
  );
};

const CartFlyout = (props) => (
  // Wrap the flyout with the needed providers
  <CartProvider query={cartQuery}>
    {(cart) => (
      <Fragment>
        <DrawerTarget id='cart-drawer'>
          {(drawer) => (
            <Drawer isOpen={drawer.isOpen} right>
              <MenuTitle onClick={drawer.hideTarget}>
                <CloseIcon icon={['fal', 'times']} /> {t('Close')}
              </MenuTitle>
              <CartFlyoutView
                isOpen={drawer.isOpen}
                modal={drawer}
                cart={cart}
                {...props}
              />
            </Drawer>
          )}
        </DrawerTarget>
      </Fragment>
    )}
  </CartProvider>
);

export default CartFlyout;
